import { getApiClient } from "@src/api";
import { whiteboardQueryKeys } from "@src/components/board/queries/query-keys";
import { graphql } from "@src/gql/gql";
import { queryClient } from "@src/query-client";
import { useMutation } from "@tanstack/react-query";
import type { Whiteboard } from "../schema";

const allowAnonymousEditQuery = graphql(/* GraphQL */ `
	mutation allowAnonymousEdit($whiteboardId: String!) {
		whiteboards {
			allowAnonymousEdit(whiteboardId: $whiteboardId) {
				causationId
				commandId
				correlationId
				id
				state {
					name
					ownerId
					status
					permissions {
						publishedLink {
							allowClone
							isReadonly
							key
						}
						userPermissions {
							key
							value
						}
					}
				}
			}
		}
	}
`);

/**
 * Allow anonymous edit on a whiteboard.
 *
 * @returns React Query mutation object
 */
export const useAllowAnonymousEdit = () => {
	const client = getApiClient();

	return useMutation({
		mutationFn: async (whiteboardId: string) => {
			const result = await client.mutate({
				mutation: allowAnonymousEditQuery,
				variables: {
					whiteboardId,
				},
			});

			return result.data;
		},
		onSuccess: (data, whiteboardId) => {
			const queryData = queryClient.getQueryData<Whiteboard>(
				whiteboardQueryKeys.ofId(whiteboardId),
			);

			const publication =
				data?.whiteboards?.allowAnonymousEdit?.state?.permissions.publishedLink;

			if (queryData && publication) {
				queryClient.setQueryData(whiteboardQueryKeys.ofId(whiteboardId), {
					...queryData,
					publication: {
						isPublished: true,
						key: publication.key,
						allowClone: publication.allowClone,
						isReadonly: publication.isReadonly,
					},
				});
			}
		},
	});
};
